import {
  StyleSheet as PDFStyleSheet,
  Text as PDFText,
  View
} from '@react-pdf/renderer'

import { formatPromotionMinorAmount } from '../../helpers/promotion'
import { getTranslate } from '../../localization'
import { PromotionApplication } from '../../types/promotion'

const styles = PDFStyleSheet.create({
  container: {
    width: '90%',
    border: '1px solid #dedede',
    borderRadius: 4,
    padding: 10,
    marginTop: 12,
    marginBottom: 8
  },
  title: {
    fontSize: 11,
    fontWeight: 700,
    marginBottom: 6
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 4,
    gap: 8
  },
  rowRtl: {
    flexDirection: 'row-reverse'
  },
  text: {
    fontSize: 9
  },
  name: {
    flexGrow: 1,
    flexShrink: 1
  },
  rtlText: {
    textAlign: 'right'
  },
  amount: {
    fontSize: 9,
    color: '#147d3f'
  },
  message: {
    fontSize: 9,
    color: '#805b10'
  }
})

type PromotionDetailsProps = {
  application?: PromotionApplication
  rtl?: boolean
}

const PromotionDetails = ({
  application,
  rtl = false
}: PromotionDetailsProps) => {
  if (!application || !Array.isArray(application.applied)) {
    return null
  }

  if (application.applied.length === 0) {
    if (
      application.state !== 'EvaluationUnavailable' &&
      application.state !== 'ReleasePending' &&
      application.state !== 'Released'
    ) {
      return null
    }
    const message =
      application.state === 'EvaluationUnavailable'
        ? 'Promotion evaluation was unavailable for this booking.'
        : application.state === 'ReleasePending'
        ? 'Promotion release is pending.'
        : 'The promotion is no longer applied to this booking.'
    return (
      <View style={styles.container}>
        <PDFText
          style={[styles.message, { textAlign: rtl ? 'right' : 'left' }]}>
          {getTranslate(message)}
        </PDFText>
      </View>
    )
  }

  return (
    <View style={styles.container}>
      <PDFText style={[styles.title, { textAlign: rtl ? 'right' : 'left' }]}>
        {getTranslate('Promotions')}
      </PDFText>
      {application.applied.map((promotion) => (
        <View key={promotion.promotionId}>
          <View style={[styles.row, rtl ? styles.rowRtl : {}]}>
            <PDFText
              style={[styles.text, styles.name, rtl ? styles.rtlText : {}]}>
              {promotion.name}
            </PDFText>
            <PDFText style={styles.amount}>
              {`${formatPromotionMinorAmount(
                promotion.discountMinor,
                promotion.currency
              )} ${getTranslate(promotion.currency)}`}
            </PDFText>
          </View>
          {(promotion.allocations || []).map((allocation) => (
            <View
              style={[styles.row, rtl ? styles.rowRtl : {}]}
              key={allocation.roomStayReference}>
              <PDFText
                style={[styles.text, styles.name, rtl ? styles.rtlText : {}]}>
                {getTranslate('Room stay {reference}', {
                  reference: allocation.roomStayReference
                })}
              </PDFText>
              <PDFText style={styles.text}>
                {`${formatPromotionMinorAmount(
                  allocation.discountMinor,
                  allocation.currency
                )} ${getTranslate(allocation.currency)}`}
              </PDFText>
            </View>
          ))}
        </View>
      ))}
      <View style={[styles.row, rtl ? styles.rowRtl : {}]}>
        <PDFText style={[styles.title, styles.name, rtl ? styles.rtlText : {}]}>
          {getTranslate('Promotion savings')}
        </PDFText>
        <PDFText style={styles.amount}>
          {`${formatPromotionMinorAmount(
            application.totalDiscountMinor,
            application.currency
          )} ${getTranslate(application.currency)}`}
        </PDFText>
      </View>
    </View>
  )
}

export default PromotionDetails
